// Ansichten der Kasse: der laufende Bon (Entwurf) und der bezahlte Bon bzw. die Rücknahme. Beträge brutto in Cent,
// Mengen im Gebinde in Tausendstel. Gerechnet hat immer der Belegkern (Preisfindung, Pfand, Steuer, Rabatt).
import { zId } from '@shared/ids'
import { z } from 'zod'
import { zPaymentRow, zRegisterRef, zTaxRow } from './common'

export const zBasketLine = z.object({
  lineId: zId,
  pos: z.int(),
  itemId: zId,
  unitId: zId,
  sku: z.string().nullable(),
  name: z.string(),
  unitShort: z.string().nullable(),
  qty: z.int(),
  unitPriceCents: z.int(),
  discountBp: z.int(),
  /** Menge × Preis vor dem Zeilenrabatt */
  listCents: z.int(),
  /** Zeilenbetrag nach dem Zeilenrabatt */
  amountCents: z.int(),
  /** Aktionspreis – mit dem Namen der Aktion */
  promotion: z.boolean(),
  priceLabel: z.string().nullable(),
  basePriceText: z.string().nullable(),
  /** Pfand der Zeile (wird automatisch berechnet und getrennt ausgewiesen) */
  depositCents: z.int(),
  depositUnitCents: z.int(),
  minAge: z.int(),
  /**
   * Artikel mit Seriennummernpflicht: die erfassten Nummern der Position und die Stücke, die am Standort der Kasse im
   * Bestand sind (zum Antippen). null bei allen anderen Artikeln.
   */
  serial: z.object({ serialNos: z.array(z.string()), available: z.array(z.string()) }).nullish(),
  /** Nachkommastellen der Menge im Gebinde: 0 = Stückware, 2 = Meter, 3 = Kilogramm und Liter (lose Ware an der Theke) */
  qtyDecimals: z.int().min(0).max(3).optional(),
  /** Gebinde des Artikels zum Umschalten (Stück ↔ Karton) */
  units: z.array(z.object({ unitId: zId, short: z.string(), label: z.string() })),
})
export type BasketLine = z.output<typeof zBasketLine>

export const zBasketEmpties = z.object({
  lineId: zId,
  depositTypeId: zId,
  name: z.string(),
  /** Stück in Tausendstel */
  qty: z.int(),
  unitCents: z.int(),
  /** negativ: mindert den Bon */
  amountCents: z.int(),
})

export const zBasketTotals = z.object({
  /** Warenwert nach Zeilenrabatten, ohne Pfand */
  goodsCents: z.int(),
  /** Rabatt auf den Bon (negativ) */
  receiptDiscountCents: z.int(),
  /** alle Rabatte zusammen (positiv) */
  discountCents: z.int(),
  /** berechnetes Pfand */
  depositCents: z.int(),
  /** zurückgenommenes Leergut (negativ) */
  emptiesCents: z.int(),
  /** verkaufte Wertgutscheine */
  voucherCents: z.int(),
  totalCents: z.int(),
  taxes: z.array(zTaxRow),
})

export const zAgeCheck = z.object({
  /** höchstes Mindestalter der Artikel auf dem Bon: 0, 16 oder 18 */
  requiredMinAge: z.int(),
  /** bestätigt bis zu diesem Mindestalter – null = noch nicht geprüft */
  confirmedMinAge: z.int().nullable(),
  /** Stichtag für die Frage an der Kasse: „geboren am oder vor …“ */
  bornOnOrBefore: z.string().nullable(),
  confirmedByName: z.string().nullable(),
  confirmedAt: z.date().nullable(),
})

export const zBasketView = z.object({
  id: zId,
  registerId: zId,
  shiftId: zId,
  version: z.int(),
  parked: z.boolean(),
  note: z.string().nullable(),
  startedAt: z.date(),
  operatorName: z.string().nullable(),
  partner: z.object({ id: zId, number: z.string().nullable(), name: z.string() }).nullable(),
  lines: z.array(zBasketLine),
  empties: z.array(zBasketEmpties),
  vouchers: z.array(z.object({ lineId: zId, amountCents: z.int() })),
  receiptDiscountBp: z.int(),
  totals: zBasketTotals,
  ageCheck: zAgeCheck,
  discount: z.object({
    /** höchster wirksamer Rabatt auf dem Bon in Basispunkten */
    maxBp: z.int(),
    /** für diesen Bon bereits gewährt (durch eine Person mit ausreichender Grenze) */
    grantedBp: z.int(),
  }),
  /** Vorgang des Bons: Nummer, Schulungsbon, ohne Absicherung begonnen (Ausfall) */
  vorgang: z.object({ bonId: z.int(), training: z.boolean(), outage: z.boolean() }).nullable(),
})
export type BasketView = z.output<typeof zBasketView>

export const RECEIPT_LINE_KINDS = ['item', 'deposit', 'empties', 'voucher', 'discount'] as const

export const zReceiptLine = z.object({
  lineId: zId,
  pos: z.int(),
  kind: z.enum(RECEIPT_LINE_KINDS),
  itemId: zId.nullable(),
  unitId: zId.nullable(),
  parentLineId: zId.nullable(),
  sku: z.string().nullable(),
  name: z.string(),
  unitShort: z.string().nullable(),
  qty: z.int(),
  unitPriceCents: z.int(),
  discountBp: z.int(),
  amountCents: z.int(),
  taxRateBp: z.int().nullable(),
  /** Seriennummern der verkauften Stücke (Artikel mit Seriennummernpflicht) */
  serialNos: z.array(z.string()).optional(),
  /** noch zurücknehmbar, im Gebinde der Zeile (nur Warenzeilen eines Bons) */
  returnableQty: z.int(),
})
export type ReceiptLine = z.output<typeof zReceiptLine>

/** Betriebsart der Kasse: live (Echtbetrieb), test (Testumgebung), demo (Demo-Mandant), training (Schulungsbetrieb). */
export const FISCAL_MODES = ['live', 'test', 'demo', 'training'] as const
export const zFiscalMode = z.enum(FISCAL_MODES)

/** Signaturblock des Bons – Werte im Originalformat der Einheit, Zeiten zusätzlich als UTC (….000Z). */
export const zFiscalInfo = z.object({
  mode: zFiscalMode,
  /** Kennzeichnung (Demo, Testumgebung, Schulung) – null im Echtbetrieb */
  notice: z.string().nullable(),
  /** Vorgangsnummer (lückenlos je Kasse) */
  bonId: z.int(),
  /** signed: abgesichert · outage: Ausfall, ohne Absicherung · pending: Abschluss wird nach dem Ausfall wiederholt */
  status: z.enum(['signed', 'outage', 'pending']),
  /** Zeile statt des Signaturblocks bei einem Ausfall */
  outageLine: z.string().nullable(),
  /** Hinweis, dass die Absicherung nach dem Ausfall nachgeholt wurde */
  delayedLine: z.string().nullable(),
  provider: z.string(),
  env: z.string(),
  processType: z.string().nullable(),
  processData: z.string().nullable(),
  txNo: z.int().nullable(),
  signatureCounter: z.int().nullable(),
  startedAtRaw: z.string().nullable(),
  finishedAtRaw: z.string().nullable(),
  startedAtIso: z.string().nullable(),
  finishedAtIso: z.string().nullable(),
  signature: z.string().nullable(),
  unitSerial: z.string(),
  clientSerial: z.string().nullable(),
  timeFormat: z.string().nullable(),
  algorithm: z.string().nullable(),
  publicKey: z.string().nullable(),
  /** Inhalt des QR-Codes (ohne zertifizierte Einheit mit „DEMO;“ davor) */
  qr: z.string().nullable(),
})
export type FiscalInfo = z.output<typeof zFiscalInfo>

export const zReceiptView = z.object({
  id: zId,
  type: z.string(),
  typeTitle: z.string(),
  number: z.string().nullable(),
  status: z.string(),
  statusLabel: z.string(),
  documentDate: z.string(),
  startedAt: z.date(),
  finishedAt: z.date().nullable(),
  register: zRegisterRef,
  shiftId: zId,
  operatorName: z.string().nullable(),
  partner: z.object({ id: zId, number: z.string().nullable(), name: z.string() }).nullable(),
  /** leistender Betrieb: Name und Anschrift, Steuernummer bzw. USt-IdNr. */
  seller: z.object({ name: z.string(), lines: z.array(z.string()), taxId: z.string().nullable() }),
  lines: z.array(zReceiptLine),
  totals: z.object({
    goodsCents: z.int(),
    receiptDiscountCents: z.int(),
    discountCents: z.int(),
    depositCents: z.int(),
    emptiesCents: z.int(),
    voucherCents: z.int(),
    totalCents: z.int(),
    netCents: z.int(),
    taxCents: z.int(),
    taxes: z.array(zTaxRow),
  }),
  payments: z.array(zPaymentRow),
  ageCheck: z.object({ minAge: z.int(), confirmedByName: z.string().nullable(), confirmedAt: z.string().nullable() }).nullable(),
  vouchersIssued: z.array(z.object({ code: z.string(), amountCents: z.int() })),
  fiscal: zFiscalInfo.nullable(),
  /** Kennzeichen oben und unten auf dem Bon („DEMO“, „TEST“, „TRAINING“) – null im Echtbetrieb */
  mark: z.string().nullable(),
  /** Hinweis unten auf dem Bon, dass er kein gültiger Kassenbeleg ist – null im Echtbetrieb */
  notice: z.string().nullable(),
  /** elektronischer Beleg: Pfad der öffentlichen Bonseite ('/r/…') – null, solange keiner ausgegeben ist */
  publicPath: z.string().nullable(),
  reason: z.string().nullable(),
  /** Rücknahme: der ursprüngliche Bon */
  returnOf: z.object({ id: zId, number: z.string().nullable() }).nullable(),
  /** Bon: seine Rücknahmen */
  returns: z.array(z.object({ id: zId, number: z.string().nullable(), totalCents: z.int() })),
  canReturn: z.boolean(),
})
export type ReceiptView = z.output<typeof zReceiptView>
